import type { BoardSource, ReindexRequested, ReindexScope } from '@/features/project-board/types';

/**
 * 재색인 버튼 목록 — 화면 밖에 둔다(F1: 파생 계산·표는 React 없이 부를 수 있어야 한다).
 *
 * 범위 키는 서버의 도메인 어휘(work-dir 종류)를 그대로 쓰고, 라벨만 화면 말로 바꾼다. 키를 화면에서
 * 새로 지으면 서버와 두 곳이 어긋난다.
 */
export interface ReindexTarget {
  scope: ReindexScope;
  label: string;
  hint: string;
}

export const REINDEX_TARGETS: readonly ReindexTarget[] = [
  { scope: 'all', label: '전체', hint: '그 저장소의 모든 문서를 다시 읽고 색인을 통째로 교체한다 — 지워진 work-dir 정리까지 여기서 된다' },
  { scope: 'specs', label: '에픽 · 스펙 · QA', hint: '그 저장소 specs/ 아래 work-dir과 QA 체크리스트·테스트케이스·결과 HTML을 전부 다시 읽는다' },
  { scope: 'issues', label: '이슈', hint: '그 저장소 issues/ 아래 work-dir을 전부 다시 읽는다' },
  { scope: 'incidents', label: '인시던트', hint: '그 저장소 incidents/ 아래 work-dir을 전부 다시 읽는다' },
];

export interface ReindexNote {
  tone: 'ok' | 'error';
  text: string;
}

export class ReindexFailure {
  /** 실패 사유를 화면 문구로. 서버가 본문에 이유를 담아 주므로 그대로 보여 준다. */
  static noteOf(error: unknown): ReindexNote {
    return { tone: 'error', text: error instanceof Error ? error.message : '재색인 요청 실패' };
  }
}

/**
 * 색인 설정 표의 한 줄 — **저장소 하나 × 범위 하나**.
 *
 * 재색인이 저장소를 가리게 되면서 줄의 단위가 (저장소, 범위) 쌍이 됐다. 저장소 정보(마지막 색인·커밋)는
 * 그 저장소의 첫 줄에만 싣는다 — 범위 수만큼 같은 값을 반복하면 표가 그 값을 네 번 말한다.
 */
export interface IndexSettingsRow {
  source: BoardSource;
  target: ReindexTarget;
  /** 예약을 건 시각(UTC ISO). 그 저장소·범위에 예약이 없으면 null. */
  reservedAt: string | null;
  /** 저장소가 바뀌는 첫 줄인가 — 마지막 색인·커밋 칸을 여기에만 그린다. */
  opensSource: boolean;
  /** 이 저장소가 차지하는 줄 수(rowSpan). 첫 줄에서만 쓴다. */
  sourceRowSpan: number;
}

export class IndexSettingsTable {
  /**
   * 표에 세울 줄 전부.
   *
   * 예약은 범위와 저장소가 **둘 다** 맞아야 그 줄의 것이다. 저장소를 안 가린 옛 예약(`source === null`)은
   * 전 저장소를 가리키므로 같은 범위의 모든 줄에 선다 — 그 예약이 실제로 그렇게 동작한다.
   */
  static rows(
    pending: readonly ReindexRequested[],
    sources: readonly BoardSource[],
  ): IndexSettingsRow[] {
    return sources.flatMap((source) =>
      REINDEX_TARGETS.map((target, index) => ({
        source,
        target,
        reservedAt: IndexSettingsTable.reservationOf(pending, source.key, target.scope),
        opensSource: index === 0,
        sourceRowSpan: REINDEX_TARGETS.length,
      })),
    );
  }

  static reservationOf(
    pending: readonly ReindexRequested[],
    sourceKey: string,
    scope: ReindexScope,
  ): string | null {
    const found = pending.find(
      (one) => one.scope === scope && (one.source === null || one.source === sourceKey),
    );
    return found?.requestedAt ?? null;
  }
}
